<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import * as zod from "zod";
import { useQuery, useQueryClient, useMutation } from "@tanstack/vue-query";
import { TYPE } from "vue-toastification";
import { useDebounceFn } from "@vueuse/core";
import { toast } from "@/helpers/toast";

import { useAppMode } from "@/composables/useAppMode";
import { getNextAction } from "@/api/getNextAction";

import NewPageWideLayout from "@/components/layouts/NewPageWide.vue";
import NavigationPane from "@/components/navigationPaneV2/NavigationPane.vue";
import PageHeader from "@/components/header/PageHeader.vue";
import BackButton from "@/components/header/BackButton.vue";
import UiButton from "@/components/ui/button/Button.vue";
import UiInput from "@/components/shared/UiInput.vue";
import UiPanel from "@/components/shared/UiPanel.vue";
import UiPanelWrapper from "@/components/shared/UiPanelWrapper.vue";
import SectionTableSvg from "@/assets/icons/i-table.svg";
import SectionGameSvg from "@/assets/icons/i-controls.svg";
import AttentionSvg from "@/assets/icons/attention.svg";

import DrillFormSection from "@/components/drills/form/DrillFormSection.vue";
import DrillFormField from "@/components/drills/form/DrillFormField.vue";
import GameFormatField from "@/components/drills/form/GameFormatField.vue";

import DrillFieldRadioGroup from "./components/fields/DrillFieldRadioGroup.vue";

import DrillEditor from "./DrillEditor.vue";
import PokerTablePreview2 from "./components/table/PokerTablePreview2.vue";
import DrillActionsList from "@/components/drills/actions/DrillActionsList.vue";

import { validate } from "@/helpers/validation";
import {
  generateCustomDrillPreviewSettings,
  generateDrillPreviewSettings,
} from "./helpers/generate-drill-preview-settings";

import {
  createDrill,
  getDrill,
  updateDrill,
  createDrillRoom,
  generateSequence,
} from "@/api/drill/index";

import {
  DrillKind,
  StopAt,
  StopAtValues,
  type DrillCreate,
} from "@acepoker/contract";
import { getMovesFromSequence } from "./helpers/get-moves-from-sequence";

const DRILL_DEFAULTS = {
  name: "",
  description: "",
  flavors: [],
  gaming_configuration: {
    game: "NLHE",
    antes: 50,
    blinds: [50, 100, 200],
  },
  max_players: 8,
  learning_mode: false,
  starting_stack: 20000,
  rebuy_configuration: {},
  stopAt: StopAt.Move,
  handsToPlay: 100,
  kind: DrillKind.Street,
  data: {},
};

const props = defineProps<{
  id?: Drill["id"];
}>();

const router = useRouter();
const { t } = useI18n();
const { mode } = useAppMode();
const queryClient = useQueryClient();

const drill = ref<DrillCreate>(DRILL_DEFAULTS);
const drillSequence = ref([]);

const isLoading = ref(Boolean(props.id));

watch(
  () => props.id,
  async () => {
    if (!props.id) {
      drill.value = DRILL_DEFAULTS;
      isLoading.value = false;
      return;
    }
    const data = await getDrill(props.id);
    drill.value = {
      ...data.drill,
      data:
        data.drill.kind === DrillKind.Sequence
          ? { street: "custom" }
          : data.drill.data,
    };
    if (data.drill.kind === DrillKind.Sequence) {
      drillSequence.value = data.sequences[0].sequence;
    }
    isLoading.value = false;
  },
  {
    immediate: true,
  },
);

const sequence = ref([]);

async function handleConfigUpdate(config) {
  drill.value.data = config;
  drill.value.kind =
    config.street === "custom" ? DrillKind.Sequence : DrillKind.Street;
  drillSequence.value = config.street === "custom" ? drillSequence.value : [];
  sequence.value =
    config.street === "custom" ? [] : await generateSequence(config);
}

watch(drillSequence, (value) => {
  if (!isCustom.value) return;
  sequence.value = getMovesFromSequence(value);
});

const isCustom = computed(() => drill.value?.data?.street === "custom");

const tableSettings = computed(() => {
  return isCustom.value
    ? generateCustomDrillPreviewSettings(drill.value, drillSequence.value)
    : generateDrillPreviewSettings(drill.value, sequence.value);
});

const drillSummaryData = ref({});

const computeDrillSummaryData = () => {
  const data = drill.value?.data || {};
  drillSummaryData.value = { ...data, sequence: sequence.value || [] };
};

const debouncedUpdateSummary = useDebounceFn(computeDrillSummaryData, 300);

watch([() => drill.value?.data, sequence], debouncedUpdateSummary, {
  immediate: true,
  deep: true,
});

const inProgress = ref(false);
const { mutate: saveDrill, isPending: isSavingPending } = useMutation({
  mutationFn: ({ drill }) => {
    const body = isCustom.value
      ? {
          drill: {
            ...drill,
            data: null,
            kind: DrillKind.Sequence,
          },
          sequence: {
            sequence: drillSequence.value,
          },
        }
      : {
          drill,
        };
    return props.id ? updateDrill(props.id, body) : createDrill(body);
  },
  onSuccess() {
    queryClient.invalidateQueries({ queryKey: ["custom-drills"] });
    toast({
      message: props.id
        ? t("CustomDrills.message.DrillUpdated", { name: drill.value.name })
        : t("CustomDrills.message.DrillCreated", { name: drill.value.name }),
      icon: TYPE.SUCCESS,
    });
  },
  onError(error) {
    inProgress.value = false;
    toast({
      message: t("Errors.label.Oops"),
      details: error.message,
      icon: TYPE.ERROR,
    });
  },
});
const { mutate: createDrillRoomMutation } = useMutation({
  mutationFn: createDrillRoom,
  onSuccess(data) {
    router.push({ name: "Room", params: { id: data.id } });
  },
  onError(error) {
    inProgress.value = false;
    console.error("Error starting drill room:", error);
  },
});

const saveDrillAndExit = async (drill) => {
  if (inProgress.value) return;

  inProgress.value = true;
  const result = saveDrill(
    { drill },
    {
      onSuccess: (data) => {
        router.push({ path: "/games/nlh/custom" });
      },
    },
  );
};
const saveDrillAndStart = async (drill) => {
  if (inProgress.value) return;

  inProgress.value = true;
  const result = saveDrill(
    { drill },
    {
      onSuccess: (data) => {
        createDrillRoomMutation(data.id);
      },
    },
  );
};

const isDetailsModalOpened = ref(false);
function toggleModal(value?: boolean) {
  // TODO: get QuintAI title

  isDetailsModalOpened.value = value ?? !isDetailsModalOpened.value;
}
const nameSchema = zod
  .string()
  .min(1, { message: "Drill name is required" })
  .min(2, { message: "Drill name must be at least 2 characters long" })
  .max(100, { message: "Drill name must be at most 100 characters long" });
const nameErrorMessage = computed(() => validate(nameSchema, drill.value.name));

const {
  status: tilesFetchingStatus,
  data: nextTile,
  error,
} = useQuery({
  queryKey: ["next-action", drillSequence],
  queryFn: async () =>
    getNextAction({
      gaming_configuration: drill.value.gaming_configuration,
      max_players: drill.value.max_players,
      starting_stack: drill.value.starting_stack,
      sequence: drillSequence.value,
    }),
  retry: 0,
});

const isSequenceCompleted = computed(() => nextTile.value?.kind === "end");

const handleDrillSummaryResponse = (response: {
  description: string;
  title: string;
}) => {
  if (response) {
    drill.value.name = response.title;
    drill.value.description = response.description;
  }
};

const stopAtOptions = StopAtValues.map((value) => ({
  value,
  title: `StopAt_${value}`,
}));

</script>

<template>
  <NewPageWideLayout>
    <template #header>
      <PageHeader>
        <template #title>{{
          props.id ? drill.name || "loading..." : $t("NewDrill.label.Title")
        }}</template>
        <template #left v-if="mode === 'mobile'">
          <BackButton :to="{ path: `/games/nlh/custom` }" class="text-white" />
        </template>
      </PageHeader>
    </template>

    <div
      class="min-h-full p-4 lg:h-full lg:p-2"
      :class="{
        '': mode === 'mobile',
        '': mode === 'desktop',
      }"
    >
      <div
        class="flex flex-grow flex-wrap lg:h-full"
        :class="{
          'bg-background-secondary rounded-xxl overflow-y-scroll':
            mode === 'mobile',
          '': mode === 'desktop',
        }"
      >
        <div
          class="bg-background-secondary lg:rounded-xxl lg:border-stroke-panel flex w-full flex-col lg:w-1/3 lg:max-w-[334px] lg:overflow-y-auto lg:border"
        >
          <div v-if="mode === 'desktop'" class="flex px-4 py-2">
            <BackButton
              :to="{ path: `/games/nlh/custom` }"
              class="text-white"
            />
          </div>
          <div v-if="isLoading" class="animate-pulse px-3 py-2 text-white">
            Loading...
          </div>
          <template v-if="!isLoading">
            <DrillFormSection>
              <template #icon><SectionTableSvg class="-mt-px" /></template>
              <template #title>{{ $t("NewDrill.label.Table") }}</template>

              <DrillFormField
                :title="$t('NewDrill.label.GameFormat')"
                :tooltip="$t('NewDrill.tooltip.GameFormat')"
              >
                <GameFormatField v-model="drill.gaming_configuration" />
              </DrillFormField>
            </DrillFormSection>

            <DrillFormSection>
              <template #icon><SectionGameSvg /></template>
              <template #title>{{ $t("NewDrill.label.Game") }}</template>

              <DrillEditor
                :config="drill.data"
                @config-updated="handleConfigUpdate"
              />
              <div
                class="my-4 flex flex-col gap-4"
                v-if="drill.data?.street == 'custom'"
              >
                <DrillFormField :title="$t('NewDrill.label.GameMode')">
                  <DrillFieldRadioGroup
                    field="stopAt"
                    :options="stopAtOptions"
                    v-model="drill.stopAt"
                  />
                </DrillFormField>

                <DrillFormField
                  :title="$t('NewDrill.label.CustomStartingSpot')"
                >
                  <DrillActionsList
                    v-if="drill.data?.street === 'custom'"
                    v-model="drillSequence"
                    :loading="tilesFetchingStatus === 'pending'"
                    :next-tile="nextTile"
                  />
                </DrillFormField>
              </div>
            </DrillFormSection>
          </template>

          <div class="mt-auto"></div>
          <div
            v-if="isSequenceCompleted"
            class="mx-auto w-[calc(100%-16px)] rounded-xl border border-[#FF7070]/10 bg-[#221212CC] p-[14px] text-sm text-[#FF7070]"
          >
            <AttentionSvg class="mb-2" />
            <p>{{ $t("NewDrill.message.SequenceCompleted") }}</p>
          </div>
          <drill-summary
            class="mx-auto w-[calc(100%-16px)]"
            :sequence="JSON.stringify(drillSequence)"
            :drillData="JSON.stringify(drillSummaryData)"
            .onResponse="handleDrillSummaryResponse"
          />
          <div v-if="mode === 'desktop'" class="w-full p-4">
            <ui-button
              type="button"
              variant="default2"
              class="w-full font-normal"
              @click="toggleModal(true)"
              :disabled="isLoading || isSequenceCompleted"
            >
              {{ $t("NewDrill.button.Save") }}
            </ui-button>
          </div>
        </div>

        <div
          v-if="!isLoading"
          class="flex w-full justify-center overflow-hidden lg:w-auto lg:flex-grow"
        >
          <PokerTablePreview2 v-bind="tableSettings" class="mx-10 mt-4" />
        </div>

        <div v-if="mode === 'mobile'" class="w-full p-4">
          <ui-button
            type="button"
            variant="default2"
            class="w-full font-normal"
            @click="toggleModal(true)"
            :disabled="isLoading || isSequenceCompleted"
          >
            {{ $t("NewDrill.button.Save") }}
          </ui-button>
        </div>
      </div>
    </div>

    <ui-panel-wrapper
      v-if="isDetailsModalOpened"
      :open="isDetailsModalOpened"
      @close="toggleModal(false)"
      modal
      blurred
    >
      <ui-panel
        :title="$t('NewDrill.label.FinishCreatingDrill')"
        :onClose="() => toggleModal(false)"
        class="w-[95vw]! md:w-[554px]!"
      >
        <p class="text-center text-sm">
          {{ $t("NewDrill.label.FinishCreatingDrillMsg") }}
        </p>

        <form
          class="flex flex-col gap-4"
          @submit.prevent="saveDrillAndStart(drill)"
        >
          <ui-input
            v-model.trim="drill.name!"
            placeholder="Drill Name"
            :error="nameErrorMessage"
          />
          <div class="input__wrapper">
            <textarea
              rows="4"
              v-model="drill.description"
              class="input__field min-h-[96px]"
              placeholder="Drill Description (optional)"
            ></textarea>
          </div>

          <div class="flex flex-col gap-2 md:flex-row md:items-center">
            <ui-button
              type="submit"
              variant="default2"
              size="lg"
              class="disabled:bg-4 w-full font-normal md:w-1/2"
              :disabled="nameErrorMessage || inProgress || isSequenceCompleted"
            >
              {{ $t("NewDrill.button.SaveAndStart") }}
            </ui-button>
            <ui-button
              type="button"
              variant="ghostOutlined"
              size="lg"
              class="w-full font-normal md:w-1/2"
              :disabled="nameErrorMessage || inProgress || isSequenceCompleted"
              @click="saveDrillAndExit(drill)"
            >
              {{ $t("NewDrill.button.Save") }}
            </ui-button>
          </div>
        </form>
      </ui-panel>
    </ui-panel-wrapper>

    <NavigationPane selectedItemId="practice" />
  </NewPageWideLayout>
</template>
